import { useState, useEffect } from "react";
import {
  Card, Table, Stack, TableRow, TableBody, TableCell, Container, Typography,
  TableContainer, Select, TextField, FormControl, InputLabel, MenuItem,
  Grid, Chip, CircularProgress, Button,
} from "@mui/material";
import Page from "src/components/Page";
import Scrollbar from "src/components/Scrollbar";
import SearchNotFound from "src/components/SearchNotFound";
import ListHead from "src/components/common/ListHead";
import ListToolbar from "src/components/common/ListToolbar";
import MyPagination from "src/components/common/MyPagination";
import { asyncRequest } from "src/services/network";
import { alert } from "src/services/alert";
import Confirm from "src/components/common/ConfirmDialog";
import SessionConfigEdit from "./SessionConfigEdit";

const TABLE_HEAD = [
  { id: "grade_id", label: "等级", alignRight: false, width: 120 },
  { id: "type", label: "类型", alignRight: false, width: 80 },
  { id: "session_time", label: "场次时间", alignRight: false, width: 180 },
  { id: "auto_issue", label: "自动出单", alignRight: false, width: 100 },
  { id: "dispatch", label: "派单", alignRight: false, width: 120 },
  { id: "robot", label: "机器人", alignRight: false, width: 120 },
  { id: "fee", label: "费率(抢购/入池/上级奖励)", alignRight: false, width: 200 },
  { id: "daily_buy_limit", label: "每日上限", alignRight: false, width: 100 },
  { id: "status", label: "状态", alignRight: false, width: 80 },
  { id: "action", label: "操作", alignRight: false, width: 150 },
];

const TYPE_OPTIONS = [
  { value: "", label: "全部" },
  { value: 1, label: "金豆" },
  { value: 2, label: "银豆" },
];

const STATUS_OPTIONS = [
  { value: "", label: "全部" },
  { value: 1, label: "启用" },
  { value: 0, label: "禁用" },
];

const TYPE_MAP = { 1: "金豆", 2: "银豆" };

function fmtTime(h, m, dur, offset) {
  const pad = (n) => String(n ?? 0).padStart(2, "0");
  const startH = Number(h || 0);
  const startM = Number(m || 0);
  const durMin = Number(dur || 0);
  const offMin = Number(offset || 0);
  const totalStart = startH * 60 + startM + offMin;
  const totalEnd = totalStart + durMin;
  const sH = Math.floor((totalStart + 24 * 60) % (24 * 60) / 60);
  const sM = (totalStart + 24 * 60) % 60;
  const eH = Math.floor((totalEnd + 24 * 60) % (24 * 60) / 60);
  const eM = (totalEnd + 24 * 60) % 60;
  return `${pad(sH)}:${pad(sM)} ~ ${pad(eH)}:${pad(eM)} (${durMin}分钟)`;
}

export default function SessionConfigList() {
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [hasQueried, setHasQueried] = useState(false);
  const [filter, setFilter] = useState({ page: 0, size: 20, grade_id: "", type: "", status: "" });
  const [grades, setGrades] = useState([]);

  const [editOpen, setEditOpen] = useState(false);
  const [editMode, setEditMode] = useState("create");
  const [editInitial, setEditInitial] = useState(null);

  // 加载等级列表
  useEffect(() => {
    asyncRequest({ path: "/jingxi/grade/all", data: {} }).then(({ data }) => {
      setGrades(data || []);
    }).catch(() => {});
  }, []);

  async function getItems(filterOverride) {
    setItems(null);
    const q = filterOverride || filter;
    const payload = {
      page: q.page,
      size: q.size,
    };
    if (q.grade_id !== "") payload.grade_id = Number(q.grade_id);
    if (q.type !== "") payload.type = Number(q.type);
    if (q.status !== "") payload.status = Number(q.status);

    const { data, count } = await asyncRequest({ path: "/jingxi/session-config/list", data: payload });
    setItems(data || []);
    setTotal(count || 0);
    setHasQueried(true);
  }

  const handlePageChange = (event, value, newSize) => {
    let newFilter;
    if (newSize !== undefined) {
      newFilter = { ...filter, page: 0, size: newSize };
    } else {
      const newPage = value - 1;
      if (newPage === filter.page) return;
      newFilter = { ...filter, page: newPage };
    }
    setFilter(newFilter);
    if (hasQueried) getItems(newFilter);
  };

  function handleReset() {
    setHasQueried(false);
    setItems(null);
    setTotal(0);
    setFilter({ page: 0, size: 20, grade_id: "", type: "", status: "" });
  }

  function openCreate() {
    setEditMode("create");
    setEditInitial(null);
    setEditOpen(true);
  }

  function openEdit(row) {
    setEditMode("edit");
    setEditInitial(row);
    setEditOpen(true);
  }

  async function handleToggleStatus(row) {
    const next = row.status === 1 ? 0 : 1;
    const { msg } = await asyncRequest({
      path: "/jingxi/session-config/toggle-status",
      data: { grade_id: row.grade_id, type: row.type, status: next },
    });
    alert(msg || "操作成功", "success");
    getItems();
  }

  async function handleReloadCache() {
    const { msg } = await asyncRequest({ path: "/jingxi/session-config/reload-cache", data: {} });
    alert(msg || "已清除缓存", "success");
  }

  async function handleDelete(row) {
    if (row.is_default) {
      return alert("默认配置不可删除，只能修改或关停");
    }
    Confirm({
      message: `确认删除等级${row.grade_id}、类型${TYPE_MAP[row.type]}的场次配置？`,
      async onConfirm() {
        try {
          const res = await asyncRequest({
            path: "/jingxi/session-config/delete",
            data: { grade_id: row.grade_id, type: row.type },
          });
          if (res.code === 1) {
            alert("删除成功", "success");
            getItems();
          } else {
            alert(res.msg || "删除失败");
          }
        } catch (err) {
          alert("删除失败");
        }
      },
    });
  }

  // 获取等级名称
  function getGradeName(gradeId) {
    const grade = grades.find(g => g.id === gradeId);
    return grade ? grade.grade_name : `等级${gradeId}`;
  }

  const isNotFound = items && items.length === 0;

  return (
    <Page title="金银通场次配置">
      <Container maxWidth="2000px">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>场次配置管理</Typography>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" onClick={handleReloadCache}>清除缓存</Button>
            <Button variant="contained" onClick={openCreate}>新建配置</Button>
          </Stack>
        </Stack>

        <Card>
          <ListToolbar showSearch={false} onReset={handleReset} onRefresh={() => getItems()}>
            <Grid container rowGap={2} alignItems="center">
              <Grid item sm={6} md={3}>
                <FormControl size="small">
                  <InputLabel>等级</InputLabel>
                  <Select label="等级" value={filter.grade_id}
                    onChange={(e) => setFilter({ ...filter, grade_id: e.target.value })} style={{ width: 200 }}>
                    <MenuItem value="">全部</MenuItem>
                    {grades.map((g) => <MenuItem key={g.id} value={g.id}>{g.grade_name}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item sm={6} md={3}>
                <FormControl size="small">
                  <InputLabel>类型</InputLabel>
                  <Select label="类型" value={filter.type}
                    onChange={(e) => setFilter({ ...filter, type: e.target.value })} style={{ width: 200 }}>
                    {TYPE_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item sm={6} md={3}>
                <FormControl size="small">
                  <InputLabel>状态</InputLabel>
                  <Select label="状态" value={filter.status}
                    onChange={(e) => setFilter({ ...filter, status: e.target.value })} style={{ width: 200 }}>
                    {STATUS_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item sm={6} md={3}>
                <Button variant="contained" size="small" onClick={() => getItems()}>查询</Button>
              </Grid>
            </Grid>
          </ListToolbar>

          <Scrollbar>
            <TableContainer>
              <Table>
                <ListHead headLabel={TABLE_HEAD} />
                {!hasQueried ? (
                  <TableBody>
                    <TableRow>
                      <TableCell colSpan={10}>
                        <Stack sx={{ width: "100%", height: 200 }} alignItems="center" justifyContent="center">
                          <Typography variant="body2" color="text.secondary">请点击查询加载数据</Typography>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                ) : !items ? (
                  <TableBody>
                    <TableRow>
                      <TableCell colSpan={10}>
                        <Stack sx={{ width: "100%", height: 200 }} alignItems="center" justifyContent="center">
                          <CircularProgress />
                        </Stack>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                ) : (
                  <TableBody>
                    {items.map((row) => (
                      <TableRow hover key={`${row.grade_id}-${row.type}`} tabIndex={-1}>
                        <TableCell align="left">
                          <Chip size="small" label={getGradeName(row.grade_id)} color="primary" />
                        </TableCell>
                        <TableCell align="left">
                          <Chip size="small" label={TYPE_MAP[row.type] || row.type}
                            color={row.type === 1 ? "warning" : "info"} />
                        </TableCell>
                        <TableCell align="left">
                          {fmtTime(row.session_start_hour, row.session_start_minute, row.session_duration_minutes, row.session_offset_minutes)}
                        </TableCell>
                        <TableCell align="left">
                          <Typography variant="body2">
                            {row.auto_issue_enabled ? `开（${row.min_system_orders}~${row.max_system_orders}单）` : "关"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {row.order_amount_mode === "FIXED" ? `固定 ${row.order_fixed_amount}` : "随机"}
                          </Typography>
                        </TableCell>
                        <TableCell align="left">
                          {row.dispatch_enabled
                            ? <Typography variant="body2">开（提前{row.dispatch_before_minutes}分钟）</Typography>
                            : <Typography variant="body2" color="text.secondary">关</Typography>}
                        </TableCell>
                        <TableCell align="left">
                          <Typography variant="body2">
                            最大{row.max_robot_orders_per_session}单 / 占比{Number(row.robot_order_ratio || 0).toFixed(2)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            自动预约: {row.robot_auto_reserve ? "开" : "关"}
                          </Typography>
                        </TableCell>
                        <TableCell align="left">
                          <Typography variant="body2">
                            {Number(row.fee_rate || 0).toFixed(2)} / {Number(row.pool_ratio || 0).toFixed(2)} / {Number(row.superior_reward_ratio || 0).toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell align="left">{row.daily_buy_limit || 0}</TableCell>
                        <TableCell align="left">
                          <Chip size="small" label={row.status === 1 ? "启用" : "禁用"}
                            color={row.status === 1 ? "success" : "default"} />
                        </TableCell>
                        <TableCell align="left">
                          <Stack direction="row" spacing={1}>
                            <Button size="small" variant="outlined" onClick={() => openEdit(row)}>编辑</Button>
                            <Button size="small" variant="outlined"
                              color={row.status === 1 ? "warning" : "success"}
                              onClick={() => handleToggleStatus(row)}>
                              {row.status === 1 ? "禁用" : "启用"}
                            </Button>
                            {!row.is_default && (
                              <Button size="small" variant="outlined" color="error"
                                onClick={() => handleDelete(row)}>删除</Button>
                            )}
                            {row.is_default && (
                              <Chip size="small" label="默认" color="warning" />
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                )}
                {isNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={10} sx={{ py: 5 }}><SearchNotFound /></TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>

          <MyPagination total={total} page={filter.page} size={filter.size} onChange={handlePageChange} />
        </Card>
      </Container>

      <SessionConfigEdit
        open={editOpen}
        mode={editMode}
        initial={editInitial}
        onClose={() => setEditOpen(false)}
        onSuccess={() => getItems()}
      />
    </Page>
  );
}
